/**
 * useSessionDeadline — Countdown reactivo de la sesión LiveKit.
 *
 * Lee `sessionEndsAt` del documento `consultas/{id}` y devuelve:
 *   - `endsAt`               : timestamp UTC en ms del vencimiento.
 *   - `remainingSec`         : segundos restantes (se actualiza cada 1s).
 *   - `extensionesUsadas`    : número de extensiones aplicadas.
 *   - `extensionesRestantes` : límite – usadas.
 *   - `warningLevel`         : 'safe' | 'warn' | 'critical' | 'expired'.
 *
 * Reglas de warning:
 *   - safe:     > 5 min restantes
 *   - warn:     ≤ 5 min  (badge en amarillo, pulsa)
 *   - critical: ≤ 1 min  (badge en rojo, pulsa más rápido)
 *   - expired:  ≤ 0 segundos (la session expiró; backend ya está cerrando)
 *
 * Si la consulta no tiene `sessionEndsAt` (legacy) devuelve `endsAt = null`.
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, type Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const MAX_EXTENSIONES = 3;

export type WarningLevel = 'safe' | 'warn' | 'critical' | 'expired';

export interface SessionDeadlineState {
  endsAt: number | null;
  remainingSec: number;
  extensionesUsadas: number;
  extensionesRestantes: number;
  sessionMaxMin: number | null;
  warningLevel: WarningLevel;
  loading: boolean;
}

export function useSessionDeadline(consultaId: string | undefined): SessionDeadlineState {
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [extensionesUsadas, setExtensionesUsadas] = useState(0);
  const [sessionMaxMin, setSessionMaxMin] = useState<number | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  // Subscribe al documento de la consulta para refletjar extensiones live.
  useEffect(() => {
    if (!consultaId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(false);
      return;
    }
    const ref = doc(db, 'consultas', consultaId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        if (!data) {
          setLoading(false);
          return;
        }
        const ts = data.sessionEndsAt as Timestamp | undefined;
        setEndsAt(ts?.toMillis?.() ?? null);
        setExtensionesUsadas(Number(data.sessionExtensionesUsadas ?? 0));
        setSessionMaxMin(Number.isFinite(data.sessionMaxMin) ? Number(data.sessionMaxMin) : null);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [consultaId]);

  // Tick cada segundo cuando hay deadline.
  useEffect(() => {
    if (endsAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  const remainingSec = useMemo(() => {
    if (endsAt === null) return 0;
    return Math.max(0, Math.floor((endsAt - now) / 1000));
  }, [endsAt, now]);

  const warningLevel: WarningLevel = useMemo(() => {
    if (endsAt === null) return 'safe';
    if (remainingSec <= 0) return 'expired';
    if (remainingSec <= 60) return 'critical';
    if (remainingSec <= 5 * 60) return 'warn';
    return 'safe';
  }, [endsAt, remainingSec]);

  return {
    endsAt,
    remainingSec,
    extensionesUsadas,
    extensionesRestantes: Math.max(0, MAX_EXTENSIONES - extensionesUsadas),
    sessionMaxMin,
    warningLevel,
    loading,
  };
}

/** Formato MM:SS de segundos restantes. */
export function formatRemaining(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
